import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { FixtureState } from '@sorare/core/src/__generated__/globalTypes';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Container,
  FullWidthScroll,
} from '@sorare/core/src/atoms/layout/Container';
import { ScarcityBackground } from '@sorare/core/src/atoms/layout/ScarcityBackground';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  LabelM,
  Text16,
  Title2,
  Title5,
} from '@sorare/core/src/atoms/typography';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { InjuryTable } from '@sorare/core/src/components/player/InjuryTable';
import { NewsTitleContent } from '@sorare/core/src/components/player/News/NewsTitleContent';
import { NewsUpdates } from '@sorare/core/src/components/player/News/NewsUpdates';
import { NBA_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { getHumanReadableSerialNumber } from '@sorare/core/src/lib/cards';
import { draft, glossary, playerDetails } from '@sorare/core/src/lib/glossary';
import {
  getPlayerInjuryColor,
  getPlayerInjuryMessage,
} from '@sorare/core/src/lib/nba';
import { absCenter } from '@sorare/core/src/lib/style';
import HandledError from '@sorare/core/src/routing/HandledError';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import CardBonus from '@sorare/marketplace/src/components/card/CardBonus';

import { CardAvatar } from '@sorare/us-sports/src/components/cards/Avatar';
import { AgeWithTooltip } from '@sorare/us-sports/src/components/player/AgeWithTooltip';
import { PlayerAvatar } from '@sorare/us-sports/src/components/player/Avatar';
import { Status } from '@sorare/us-sports/src/components/player/Status';
import { UpcomingGames } from '@sorare/us-sports/src/components/player/UpcomingGames';
import { TrackEvent, useEvents } from '@sorare/us-sports/src/hooks/useEvents';

import { FixtureChart } from 'components/player/FixtureChart';
import { PlayerPositionsWithTooltip } from 'components/player/PlayerPositionsWithTooltip';

import {
  NBAInfoModalQuery,
  NBAInfoModalQueryVariables,
} from './__generated__/index.graphql';

type NBAInfoModalQuery_nbaCard_player = NonNullable<
  NBAInfoModalQuery['nbaCard']
>['anyPlayer'];

type NBAInfoModalQuery_nbaPlayer = NonNullable<NBAInfoModalQuery['nbaPlayer']>;

const NBA_INFO_MODAL_QUERY = gql`
  query NBAInfoModalQuery(
    $cardSlug: String!
    $playerSlug: String!
    $skipCard: Boolean!
    $scoringStrategy: String
  ) {
    nbaCard(slug: $cardSlug) @skip(if: $skipCard) {
      id
      slug
      rarityTyped
      anyPlayer {
        slug
        ...player_fragment
      }
      ...CardBonus_anyCard
      ...CardAvatar_anyCard
      ...getHumanReadableSerialNumber_anyCard
    }
    nbaPlayer(slug: $playerSlug) @include(if: $skipCard) {
      slug
      displayName
      ...player_fragment
      ...UsSportsPlayerAvatar_player
    }
  }
  fragment player_fragment on NBAPlayer {
    slug
    tenGameAverageGameStats {
      score
    }
    displayName
    birthDate
    birthPlaceCountry
    age
    team {
      slug
      fullName
    }
    playerInjury {
      status
      ...InjuryTable_PlayerInjury
    }
    newsUpdates(last: 1) {
      ...NewsUpdates_PlayerNewsUpdate
    }
    upcomingFixtureStats: latestFixtureStats(last: 5) {
      fixture {
        slug
        gameWeek
        fixtureState
      }
      status {
        upcomingGames {
          id
          ...UpcomingGames_Game
        }
      }
    }
    ...FixtureChart_NBAPlayer
    ...Status_PlayerInterface
    ...NBAPlayerPositionsWithTooltip_player
  }
  ${CardAvatar.fragments.anyCard}
  ${InjuryTable.fragments.PlayerInjury}
  ${NewsUpdates.fragments.PlayerNewsUpdate}
  ${UpcomingGames.fragments.Game}
  ${FixtureChart.fragments.NBAPlayer}
  ${Status.fragments.PlayerInterface}
  ${PlayerPositionsWithTooltip.fragments.player}
  ${getHumanReadableSerialNumber.fragments.anyCard}
  ${PlayerAvatar.fragments.player}
  ${CardBonus.fragments.anyCard}
` as TypedDocumentNode<NBAInfoModalQuery, NBAInfoModalQueryVariables>;

function isPlayerWithAvatar(
  player: NBAInfoModalQuery_nbaPlayer | NBAInfoModalQuery_nbaCard_player
): player is NBAInfoModalQuery_nbaPlayer {
  return (player as NBAInfoModalQuery_nbaPlayer).avatarPictureUrl !== undefined;
}
const Wrapper = styled(Vertical).attrs({ gap: 4 })`
  color: var(--c-neutral-800);
`;

const Header = styled(Vertical).attrs({ gap: 0 })`
  background: linear-gradient(
    358deg,
    transparent 0%,
    transparent 66.67%,
    var(--c-neutral-300) 100%
  );

  color: var(--c-neutral-1000);
  position: relative;
  justify-content: stretch;
`;

const InjuryBanner = styled(Horizontal).attrs({ center: true })`
  padding: var(--unit);
  text-transform: uppercase;
  &.yellow {
    --injury-rgb: var(--c-rgb-yellow-600);
  }
  &.red {
    --injury-rgb: var(--c-rgb-red-600);
  }
  color: rgba(var(--injury-rgb));
  background: linear-gradient(
    160deg,
    rgba(var(--injury-rgb), 0.16) 0%,
    rgba(var(--injury-rgb), 0) 100%
  );
`;

const HeaderContent = styled(Vertical).attrs({ gap: 0, center: true })`
  padding: var(--double-unit);
`;

const DotSeparated = styled.div`
  & > * {
    display: inline;
  }
  & > * + *::before {
    content: ' • ';
  }
`;

const CardAvatarWithScoreOnTop = styled.div`
  position: relative;
  margin-bottom: var(--quadruple-unit);
`;

const RoundedCardAvatar = styled(CardAvatar)`
  border-radius: var(--double-unit);
  overflow: hidden;
  width: clamp(calc(5 * var(--unit)), calc(15 * var(--unit)), 25vw);
`;

const RoundedPlayerAvatar = styled(PlayerAvatar)`
  border-radius: var(--double-unit);
  overflow: hidden;
  width: clamp(calc(5 * var(--unit)), calc(15 * var(--unit)), 25vw);
`;

const TenGameAverageWrapper = styled.div`
  ${absCenter}
  transform-origin: center;
  top: 100%;
  @media ${tabletAndAbove} {
    transform: translate(-50%, -50%) scale(1.5);
  }
`;
const PlayerName = styled(Title2)`
  color: var(--c-neutral-1000);
  text-align: center;
`;
const Close = styled.div`
  position: absolute;
  right: var(--double-unit);
  top: var(--double-unit);
`;

const CardBonusAndSerial = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--unit) 0;
`;

const Content = styled(Vertical).attrs({ gap: 4 })`
  padding-bottom: var(--quadruple-unit);
`;

const LoadingWrapper = styled(Horizontal).attrs({ gap: 0 })`
  min-height: 75vh;
`;

const InfoModalContainer = styled(Container)`
  --container-padding: var(--double-unit);
  gap: var(--unit);
`;

const InlineTitle = styled.div`
  display: flex;
  gap: var(--unit);
  align-items: baseline;
`;

const Block = styled(LabelM).attrs({ as: 'div' })`
  padding: var(--double-unit);
  background: var(--c-neutral-300);
  border-radius: var(--double-unit);
`;

const NewsBlock = styled(Block)`
  padding-bottom: var(--intermediate-unit);
`;

// On common daily drop card we don't have a card slug
type Props = {
  cardSlug?: string;
  onClose?: () => void;
  playerSlug?: string;
  displayNameWithoutRarity?: string;
  scoringStrategy?: string;
  withPlayerLink?: boolean;
  trackValue?: TrackEvent;
};

export const InfoModal = ({
  cardSlug = '',
  onClose,
  playerSlug = '',
  scoringStrategy = 'default',
  displayNameWithoutRarity,
  withPlayerLink = false,
  trackValue,
}: Props) => {
  const track = useEvents();

  useEffect(() => {
    if (cardSlug !== '' && trackValue) {
      track(trackValue);
    }
  }, [track, cardSlug, trackValue]);

  const { formatMessage } = useIntl();
  const { data, loading } = useQuery(NBA_INFO_MODAL_QUERY, {
    variables: {
      cardSlug,
      playerSlug,
      scoringStrategy,
      skipCard: !!playerSlug,
    },
    skip: !cardSlug && !playerSlug,
  });
  if (loading || !data) {
    return (
      <LoadingWrapper>
        <LoadingIndicator />
      </LoadingWrapper>
    );
  }

  const { nbaCard: card, nbaPlayer } = data;

  const player =
    nbaPlayer || (isType(card?.anyPlayer, 'NBAPlayer') && card.anyPlayer);

  if (!player) {
    return (
      <HandledError
        code={404}
        message={formatMessage({
          id: 'InfoModal.notFound',
          defaultMessage: "There was an error getting this Card's data",
        })}
      />
    );
  }

  const { playerInjury: injury } = player;

  return (
    <Wrapper>
      <div>
        <Header>
          {onClose && (
            <Close>
              <IconButton
                onClick={onClose}
                icon={faTimes}
                color="tertiary"
                aria-label={formatMessage(glossary.close)}
              />
            </Close>
          )}

          {injury && (
            <InjuryBanner className={getPlayerInjuryColor(injury.status)}>
              <LabelM as="span" bold>
                <FormattedMessage
                  {...getPlayerInjuryMessage(injury.status, { long: true })}
                />
              </LabelM>
            </InjuryBanner>
          )}

          <HeaderContent>
            <CardAvatarWithScoreOnTop>
              {card ? (
                <RoundedCardAvatar card={card} />
              ) : (
                isPlayerWithAvatar(player) && (
                  <RoundedPlayerAvatar player={player} />
                )
              )}
              <TenGameAverageWrapper>
                <Cost
                  score={player.tenGameAverageGameStats.score}
                  title={formatMessage(draft.nbaTenGameAverageScoreTitle, {
                    score: player.tenGameAverageGameStats.score,
                  })}
                />
              </TenGameAverageWrapper>
            </CardAvatarWithScoreOnTop>
            {withPlayerLink ? (
              <PlayerName
                as={Link}
                to={generatePath(NBA_PLAYERS_SLUG_CARDS, { slug: player.slug })}
              >
                {player.displayName}
              </PlayerName>
            ) : (
              <PlayerName>{player.displayName}</PlayerName>
            )}
            <LabelM as="span">
              <Horizontal>
                <DotSeparated>
                  {player.team && <span>{player.team.fullName}</span>}
                  <div>
                    <PlayerPositionsWithTooltip player={player} />
                  </div>
                  <AgeWithTooltip
                    birthDate={player.birthDate}
                    age={player.age}
                  />
                  <span>{player.birthPlaceCountry}</span>
                </DotSeparated>
              </Horizontal>
            </LabelM>
            <Status player={player} />
          </HeaderContent>
        </Header>
        {card && (
          <ScarcityBackground className={card.rarityTyped}>
            <CardBonusAndSerial>
              <LabelM as="div">
                <Horizontal>
                  <CardBonus card={card} />
                  <LabelM bold>
                    <span>
                      {getHumanReadableSerialNumber(card, {
                        separator: ' • ',
                      })}
                    </span>
                  </LabelM>
                </Horizontal>
              </LabelM>
            </CardBonusAndSerial>
          </ScarcityBackground>
        )}
      </div>
      <Content>
        {player.playerInjury && (
          <InfoModalContainer>
            <Title5>
              <FormattedMessage {...playerDetails.injuryReport} />
            </Title5>
            <Block>
              <InjuryTable
                injury={player.playerInjury}
                getPlayerInjuryColor={getPlayerInjuryColor}
              />
            </Block>
          </InfoModalContainer>
        )}
        {player.newsUpdates && (
          <InfoModalContainer>
            <Title5>
              <NewsTitleContent />
            </Title5>
            <NewsBlock>
              <NewsUpdates
                updates={player.newsUpdates}
                playerName={player.displayName}
                hideAnalysis
              />
            </NewsBlock>
          </InfoModalContainer>
        )}
        <InfoModalContainer>
          <Title5>
            <Horizontal>
              <FormattedMessage {...playerDetails.upcoming_games} />
            </Horizontal>
          </Title5>
          <FullWidthScroll>
            <UpcomingGames
              games={player!.upcomingFixtureStats
                .filter(
                  playerInFixture =>
                    playerInFixture.fixture.fixtureState === FixtureState.opened
                )
                .map(fixture => fixture.status.upcomingGames)
                .flat()}
              playerTeamSlug={player!.team?.slug}
              scrollSnap
            />
          </FullWidthScroll>
        </InfoModalContainer>

        {scoringStrategy !== 'nba_all_star_saturday_night' && (
          <InfoModalContainer>
            <InlineTitle>
              <Title5>
                <FormattedMessage {...playerDetails.performance} />
              </Title5>
              {scoringStrategy !== 'default' && (
                <Text16>({displayNameWithoutRarity})</Text16>
              )}
            </InlineTitle>
            <FullWidthScroll>
              <FixtureChart player={player} />
            </FullWidthScroll>
          </InfoModalContainer>
        )}
      </Content>
    </Wrapper>
  );
};
